import DocsLayout, { FeatureCard, Callout } from '../../components/docs/DocsLayout';
import { Bot, Settings, Wallet, Shield } from 'lucide-react';

export default function DocsAgents() {
  return (
    <DocsLayout 
      title="Agents" 
      description="Understanding AI agents and autonomy levels"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          Agents are the core building block of AIBL. Each agent is an autonomous entity with its own wallet, permissions, and identity. Agents can execute DeFi operations based on the rules you configure.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Agent Components</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <FeatureCard
            icon={<Bot size={24} className="text-blue-400" />}
            title="Identity"
            description="Unique identifier, name, and metadata that define your agent across the platform."
          />
          <FeatureCard
            icon={<Wallet size={24} className="text-green-400" />}
            title="Wallet"
            description="ERC-4337 smart account with programmable access control and batched transactions."
          />
          <FeatureCard
            icon={<Settings size={24} className="text-purple-400" />}
            title="Plugins"
            description="Enabled capabilities that define what operations the agent can perform."
          />
          <FeatureCard
            icon={<Shield size={24} className="text-yellow-400" />}
            title="Limits"
            description="Spending caps and operational boundaries that protect against unexpected behavior."
          />
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Autonomy Levels</h2>
        
        <p className="text-white/70 mb-6">
          Agents operate at different autonomy levels based on how much independent decision-making they can perform:
        </p>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-semibold">L0</span>
              <h3 className="font-semibold text-white">Manual</h3>
            </div>
            <p className="text-sm text-white/60">
              No autonomous operation. All transactions require explicit API calls from your application.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-semibold">L1</span>
              <h3 className="font-semibold text-white">Reactive</h3>
            </div>
            <p className="text-sm text-white/60">
              Agent responds to predefined triggers and conditions. Executes when specific on-chain events occur.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-semibold">L2</span>
              <h3 className="font-semibold text-white">Proactive</h3>
            </div>
            <p className="text-sm text-white/60">
              Agent can initiate operations based on market conditions and optimization goals you define.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 text-xs font-semibold">L3</span>
              <h3 className="font-semibold text-white">Autonomous</h3>
            </div>
            <p className="text-sm text-white/60">
              Full autonomy within permission boundaries. Agent optimizes portfolio and executes strategies independently.
            </p>
          </div>
        </div>

        <Callout type="warning" title="Start with Lower Autonomy">
          We recommend starting at L0 or L1 and gradually increasing autonomy as you gain confidence in your agent's behavior and your permission configuration.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Creating an Agent</h2>
        
        <p className="text-white/70">
          Agents can be created through the dashboard or via API. Each agent is associated with your developer account and inherits your account-level settings.
        </p>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Via Dashboard</h3>
        <ol className="space-y-2 text-white/70">
          <li>1. Navigate to the Dashboard</li>
          <li>2. Click "Create Agent"</li>
          <li>3. Enter a name and select target chain</li>
          <li>4. Configure initial plugin settings</li>
          <li>5. Fund the agent wallet</li>
        </ol>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Via API</h3>
        <p className="text-white/70">
          See the <a href="/docs/api/endpoints" className="text-blue-400 hover:underline">API Reference</a> for programmatic agent creation.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Agent Limits</h2>
        
        <p className="text-white/70 mb-4">
          Every agent has configurable spending limits to protect against unexpected behavior:
        </p>

        <ul className="space-y-2 text-white/70">
          <li><strong className="text-white">Per-Transaction Limit</strong>: Maximum value for a single operation</li>
          <li><strong className="text-white">Daily Limit</strong>: Total value that can be moved in 24 hours</li>
          <li><strong className="text-white">Monthly Limit</strong>: Aggregate cap for the calendar month</li>
        </ul>
      </section>
    </DocsLayout>
  );
}
